import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi, type UserInfo, type LoginParams } from '@/api'
import { ElMessage } from 'element-plus'

export const useUserStore = defineStore('user', () => {
    // 状态
    const token = ref<string>(localStorage.getItem('token') || '')
    const userInfo = ref<UserInfo | null>(
        localStorage.getItem('userInfo')
            ? JSON.parse(localStorage.getItem('userInfo')!)
            : null
    )
    const loading = ref(false)

    // 计算属性
    const isLoggedIn = computed(() => !!token.value)
    const hasPermission = computed(() => (permission: string) => {
        return userInfo.value?.permissions?.includes(permission) || false
    })
    const hasRole = computed(() => (role: string) => {
        return userInfo.value?.roles?.includes(role) || false
    })

    // 方法
    const login = async (params: LoginParams) => {
        loading.value = true
        try {
            const response = await authApi.login(params)

            token.value = response.token
            userInfo.value = response.adminInfo

            // 持久化存储
            localStorage.setItem('token', response.token)
            localStorage.setItem('userInfo', JSON.stringify(response.adminInfo))

            ElMessage.success('登录成功')
            return response
        } catch (error) {
            console.error('登录失败:', error)
            throw error
        } finally {
            loading.value = false
        }
    }

    const logout = async () => {
        try {
            await authApi.logout()
        } catch (error) {
            console.error('登出失败:', error)
        } finally {
            // 清除状态和本地存储
            token.value = ''
            userInfo.value = null
            localStorage.removeItem('token')
            localStorage.removeItem('userInfo')

            ElMessage.success('已退出登录')
        }
    }

    const fetchUserInfo = async () => {
        try {
            const info = await authApi.getUserInfo()
            userInfo.value = info
            localStorage.setItem('userInfo', JSON.stringify(info))
            return info
        } catch (error) {
            console.error('获取用户信息失败:', error)
            // 如果获取用户信息失败，清除登录状态
            logout()
            throw error
        }
    }

    return {
        // 状态
        token,
        userInfo,
        loading,

        // 计算属性
        isLoggedIn,
        hasPermission,
        hasRole,

        // 方法
        login,
        logout,
        fetchUserInfo
    }
})
